// 108 heures — formatage et analyse des durées, année scolaire, document imprimé.
// Les durées s'affichent toujours en h et min (« 1h30 »), jamais en décimal.

import { printFontFaces } from '$lib/printFonts';
import type { HoursSummary } from '$lib/api';

/** Libellés courts pour les pastilles et colonnes ; le libellé long reste au récapitulatif. */
export const CATEGORIE_COURTE: Record<string, string> = {
	apc: 'APC',
	equipe: 'Équipe',
	formation: 'Formation',
	conseil_ecole: "Conseil d'école"
};

export function categorieCourte(key: string, label: string): string {
	return CATEGORIE_COURTE[key] ?? label;
}

export function formatDuree(minutes: number): string {
	const m = Math.max(0, Math.round(minutes));
	const h = Math.floor(m / 60);
	const r = m % 60;
	if (h === 0) return `${r} min`;
	if (r === 0) return `${h}h`;
	return `${h}h${String(r).padStart(2, '0')}`;
}

/** « 1h30 », « 1 h », « 1:30 », « 90 », « 1,5 », « 45 min » → minutes, ou null. Miroir de hours.py. */
export function parseDuree(text: string): number | null {
	const s = text.toLowerCase().replace(/\s+/g, '');
	if (!s) return null;
	let m = s.match(/^(\d+)h(\d{1,2})?(min)?$/);
	if (m) return Number(m[1]) * 60 + Number(m[2] ?? 0);
	m = s.match(/^(\d+):(\d{1,2})$/);
	if (m) return Number(m[1]) * 60 + Number(m[2]);
	m = s.match(/^(\d+)(min|mn|m)$/);
	if (m) return Number(m[1]);
	m = s.match(/^(\d+)[.,](\d+)h?$/);
	if (m) return Math.round(Number(`${m[1]}.${m[2]}`) * 60);
	if (/^\d+$/.test(s)) return Number(s);
	return null;
}

export function anneeScolaireDe(dateStr: string): string {
	const year = Number(dateStr.slice(0, 4));
	const month = Number(dateStr.slice(5, 7));
	return month >= 9 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
}

export function aujourdhuiIso(): string {
	const d = new Date();
	const p = (n: number) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function formatDateCourte(iso: string): string {
	const [y, m, d] = iso.split('-').map(Number);
	return new Date(y, m - 1, d).toLocaleDateString('fr-FR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	});
}

function esc(s: string): string {
	return s
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/**
 * Document A4 portrait remis à l'IEN : en-tête, récapitulatif par catégorie,
 * détail chronologique dense, bloc signature. Écrit dans une fenêtre
 * `window.open()` puis imprimé, comme les évaluations et les progressions.
 */
export function documentImpression(s: HoursSummary): string {
	const st = s.settings;
	const identite = [
		['Nom', st.nom],
		['École', st.ecole],
		['Circonscription', st.circonscription],
		['Année scolaire', s.school_year],
		...(st.quotite !== 100 ? [['Quotité', `${st.quotite} %`]] : []),
		...(Object.keys(st.target_overrides).length > 0 ? [['Volumes attendus', 'ajustés (voir tableau)']] : [])
	]
		.map(
			([k, v]) =>
				`<div class="id-row"><span class="id-k">${esc(k)}</span><span class="id-v">${v ? esc(v) : '<span class="blank"></span>'}</span></div>`
		)
		.join('');

	const recap = s.categories
		.map(
			(c) => `<tr>
	<td>${esc(c.label)}</td>
	<td class="num">${formatDuree(c.target_minutes)}</td>
	<td class="num">${formatDuree(c.done_minutes)}</td>
	<td class="num">${c.target_minutes === 0 ? '—' : formatDuree(c.remaining_minutes)}</td>
</tr>`
		)
		.join('');

	const labels = Object.fromEntries(
		s.categories.map((c) => [c.key, categorieCourte(c.key, c.label)])
	);
	const detail = [...s.entries]
		.sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
		.map(
			(e) => `<tr>
	<td class="date">${formatDateCourte(e.date)}</td>
	<td>${esc(labels[e.category_key] ?? e.category_key)}</td>
	<td>${e.subtype ? esc(e.subtype) : ''}</td>
	<td>${esc(e.title)}${e.comment ? `<span class="note"> — ${esc(e.comment)}</span>` : ''}</td>
	<td class="num">${formatDuree(e.duration_minutes)}</td>
</tr>`
		)
		.join('');

	return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8">
<title>108 heures — ${esc(s.school_year)}</title>
<style>
${printFontFaces()}
/* Document imprimé A4, aux hex du design system (les tokens CSS ne résolvent pas
   dans la fenêtre d'impression détachée). Typographie compacte : une année
   complète doit tenir sur 1 à 2 pages. */
@page { size: A4 portrait; margin: 1.2cm 1.4cm; }
body { font-family: 'DM Sans', Arial, sans-serif; font-size: 8.5pt; line-height: 1.25; color: #11131d; margin: 0; }
h1 { font-family: 'Lora', Georgia, serif; font-weight: 620; font-size: 13pt; margin: 0 0 1pt; }
.sub { font-size: 8pt; color: #525252; margin: 0 0 8pt; }
.identite { display: grid; grid-template-columns: 1fr 1fr; gap: 1pt 18pt; margin-bottom: 8pt; }
.id-row { display: flex; gap: 6pt; align-items: baseline; }
.id-k { color: #525252; font-size: 7.5pt; width: 70pt; flex: none; }
.id-v { flex: 1; border-bottom: 1px solid #dedede; padding-bottom: 1pt; min-height: 10pt; }
.blank { display: inline-block; width: 100%; }
h2 { font-family: 'Lora', Georgia, serif; font-weight: 620; font-size: 10.5pt; margin: 8pt 0 4pt; }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid #dedede; padding: 2pt 5pt; vertical-align: top; text-align: left; }
th { background-color: #f2f2f2; font-weight: 600; font-size: 7.5pt; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
thead { display: table-header-group; }
tr { page-break-inside: avoid; }
.num { text-align: right; white-space: nowrap; }
.date { white-space: nowrap; }
.total td { font-weight: 600; background-color: #e3ebfd; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
.detail td { font-size: 7.5pt; padding: 1pt 4pt; }
.note { color: #525252; }
.signature { display: grid; grid-template-columns: 1fr 1fr; gap: 24pt; margin-top: 12pt; page-break-inside: avoid; }
.sig-box { border: 1px solid #dedede; height: 50pt; padding: 4pt 6pt; font-size: 7.5pt; color: #525252; }
.empty { color: #525252; font-style: italic; }
</style>
</head>
<body>
<h1>Récapitulatif des 108 heures</h1>
<p class="sub">Obligations de service hors enseignement — année scolaire ${esc(s.school_year)}</p>
<div class="identite">${identite}</div>

<h2>Récapitulatif par catégorie</h2>
<table>
<thead><tr><th>Catégorie</th><th class="num">Cible</th><th class="num">Effectué</th><th class="num">Restant</th></tr></thead>
<tbody>
${recap}
<tr class="total"><td>Total</td><td class="num">${formatDuree(s.total.target_minutes)}</td><td class="num">${formatDuree(s.total.done_minutes)}</td><td class="num">${formatDuree(s.total.remaining_minutes)}</td></tr>
</tbody>
</table>

<h2>Détail chronologique</h2>
${
	s.entries.length
		? `<table class="detail">
<thead><tr><th>Date</th><th>Catégorie</th><th>Sous-type</th><th>Intitulé</th><th class="num">Durée</th></tr></thead>
<tbody>${detail}</tbody>
</table>`
		: '<p class="empty">Aucune heure saisie.</p>'
}

<div class="signature">
	<div class="sig-box">Fait à ______________________, le ____ / ____ / ________</div>
	<div class="sig-box">Signature de l'enseignant·e</div>
</div>
<script>window.onload = function() {
  var printed = false;
  var go = function() { if (printed) return; printed = true; window.print(); window.close(); };
  if (document.fonts && document.fonts.ready) { document.fonts.ready.then(go, go); setTimeout(go, 3000); }
  else { setTimeout(go, 500); }
}<\/script>
</body>
</html>`;
}

/**
 * Le journal de l'année en CSV pour tableur (séparateur « ; », BOM UTF-8,
 * dates au format français) — les mêmes colonnes que l'import, plus le
 * sous-type, pour qu'un export puisse être réimporté tel quel.
 */
export function exportCsv(s: HoursSummary): Blob {
	const labels = Object.fromEntries(
		s.categories.map((c) => [c.key, categorieCourte(c.key, c.label)])
	);
	const cell = (v: string) => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
	const lignes = [['date', 'categorie', 'duree', 'intitule', 'commentaire', 'sous-type']];
	for (const e of [...s.entries].sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)) {
		lignes.push([
			formatDateCourte(e.date),
			labels[e.category_key] ?? e.category_key,
			formatDuree(e.duration_minutes),
			e.title,
			e.comment ?? '',
			e.subtype ?? ''
		]);
	}
	const texte = lignes.map((l) => l.map(cell).join(';')).join('\r\n') + '\r\n';
	return new Blob(['﻿' + texte], { type: 'text/csv;charset=utf-8' });
}
